import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import OptimizedImage from "./OptimizedImage";
import { useLanguage } from "@/lib/LanguageContext";

// Define image content keys
const imageKeys = [
  {
    url: "/5O5A0010w.webp",
    captionKey: "murchisonFallsCaption",
    descriptionKey: "murchisonFallsDescription"
  },
  {
    url: "/masaimaragreatmigration.webp",
    captionKey: "maasaiMaraCaption",
    descriptionKey: "maasaiMaraDescription"
  },
  {
    url: "/zebra-serengeti.webp",
    captionKey: "serengetiCaption",
    descriptionKey: "serengetiDescription"
  },
  {
    url: "/gorilla-trekking.webp",
    captionKey: "volcanoesCaption",
    descriptionKey: "volcanoesDescription"
  }
];

export default function ImageSlider() {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === imageKeys.length - 1 ? 0 : prevIndex + 1
      );
    }, 8000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="relative w-full h-full max-h-screen overflow-hidden"
      onTouchStart={(e) => {
        const touch = e.touches[0];
        const target = e.currentTarget;
        target.dataset.touchStartX = touch.clientX.toString();
        target.dataset.touchEndX = touch.clientX.toString();
      }}
      onTouchMove={(e) => {
        const touch = e.touches[0];
        e.currentTarget.dataset.touchEndX = touch.clientX.toString();
      }}
      onTouchEnd={(e) => {
        const target = e.currentTarget;
        const startX = parseFloat(target.dataset.touchStartX || "0");
        const endX = parseFloat(target.dataset.touchEndX || "0");
        const distance = startX - endX;
        const minSwipeDistance = 50;

        if (distance > minSwipeDistance) {
          setCurrentIndex((prev) => (prev === imageKeys.length - 1 ? 0 : prev + 1));
        } else if (distance < -minSwipeDistance) {
          setCurrentIndex((prev) => (prev === 0 ? imageKeys.length - 1 : prev - 1));
        }
      }}
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          {/* Background image with OptimizedImage - all hero images are priority */}
          <div className="absolute inset-0">
            <OptimizedImage
              src={imageKeys[currentIndex].url}
              alt={t(imageKeys[currentIndex].captionKey)}
              className="w-full h-full"
              objectFit="cover"
              priority={true} // All hero images should load with priority
              onLoad={() => setIsLoading(false)}
              onError={(e) => {
                console.error('Image failed to load:', imageKeys[currentIndex].url);
                e.currentTarget.src = '/placeholder-image.jpg';
              }}
            />
          </div>

          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/40" />

          {/* Content container */}
          <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
            {/* Text content removed as per new requirements */}

            {/* Slide indicators */}
            <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex gap-2 pointer-events-auto z-50">
              {imageKeys.map((_, index) => (
                <button
                  key={index}
                  className={`transition-all duration-300 rounded-full relative z-30 ${index === currentIndex
                      ? "w-4 sm:w-6 h-1.5 sm:h-2 bg-white"
                      : "w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/50 hover:bg-white/70"
                    }`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}